// src/pages/healthcare/appointments/components/PractitionerCalendar.tsx
import React, { useCallback, useMemo, useRef } from "react";
import { Calendar, CalendarProps, SlotInfo } from "react-big-calendar";
import withDragAndDrop, { EventInteractionArgs } from "react-big-calendar/lib/addons/dragAndDrop";
import { StopOutlined } from "@ant-design/icons";
import { ScheduleOverride, TeamMember, UnavailableBlock } from "../../../../types/admin/TeamTypes";
import PractitionerAvatar, { NEUTRAL_BACKGROUND } from "../../../../components/shared/PractitionerAvatar";
import { practitionerColour } from "../../../../utils/appointments/practitionerColour";
import {
	BlockRef,
	DayAvailability,
	MINUTES_PER_DAY,
	UnavailableSource,
	blockRefKey,
	intervalsContain,
	mergeIntervals,
	normaliseBlockType,
	resolveDayAvailability,
	subtractIntervals,
	toDateKey,
} from "../../../../utils/appointments/availability";
import { canRescheduleAppointment } from "../../../../utils/appointments/reschedule";
import { getReadableTextColor } from "../../../../utils/colorUtils";
import { dayjs } from "../../../../utils/time";
import { formatLocalTime } from "../../../../utils/userPrefs/DateTimeUtils";
import { calendarLocalizer } from "./calendarLocalizer";
import UnavailableBlockCard, { BlockVisual, blockClassName, blockLabel } from "./UnavailableBlockCard";
import { SlotSelection } from "./SlotActionPopover";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "../AppointmentPage.css";
import type { UserUuid } from "../../../../types/identity";

interface Event {
	id: number;
	title: string;
	start: Date;
	end: Date;
	resource: {
		appointment: any;
	};
}

interface UnavailableEvent {
	id: string;
	title: string;
	start: Date;
	end: Date;
	resource: {
		unavailable: BlockVisual;
		columnIds: UserUuid[];
		// null for a whole-day-off region, which is an override rather than a stored block.
		block: BlockRef | null;
		// Set only on the all-staff week's foreground cards; null for closures and day-off regions.
		practitionerId: UserUuid | null;
	};
}

type CalendarEvent = Event | UnavailableEvent;

const isUnavailable = (event: CalendarEvent): event is UnavailableEvent => "unavailable" in event.resource;

const unavailableEvent = (day: Date, ids: UserUuid[], visual: BlockVisual, start: number, end: number, key: string, ref: BlockRef | null, practitionerId: UserUuid | null): UnavailableEvent => {
	// Minutes off local midnight rather than epoch arithmetic, so a DST day still lands on the right wall clock.
	const at = (minutes: number) => new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, minutes);
	return { id: key, title: blockLabel(visual), start: at(start), end: at(end), resource: { unavailable: visual, columnIds: ids, block: ref, practitionerId } };
};

// One shape for both kinds so S12b can add the appointment branch to the same handler.
export interface EventMove {
	start: Date;
	end: Date;
	// The column the drop landed in; null in the week view, which has none.
	toColumnId: UserUuid | null;
	action: "move" | "resize";
}

export type CalendarViewMode = "day" | "week-all";

// RBC groups events into columns through a Map, so an event matching no column is dropped with no error.
const UNASSIGNED_RESOURCE_ID = "unassigned";

// The week view has no per-practitioner column, so its shading is keyed on one aggregate row instead.
const AGGREGATE_COLUMN = "all";

const indexKey = (columnId: string, day: Date): string => `${columnId}|${toDateKey(day)}`;

// RBC's types omit "week" from startOf, but the localizer is what pins Monday — going through it keeps these days identical to the ones RBC renders.
const startOfCalendarWeek = calendarLocalizer.startOf as unknown as (date: Date, unit: string) => Date;

// The five values the backend accepts, each of which divides one labelled hour row cleanly.
const SLOT_CHOICES = [5, 10, 15, 20, 30];

export const resolveSlotMinutes = (value?: number | null): number => (value && SLOT_CHOICES.includes(value) ? value : 15);

interface ResourceColumn {
	id: string;
	member: TeamMember | null;
	title: string;
}

interface DayColumnBodyProps {
	children?: React.ReactNode;
	className?: string;
	style?: React.CSSProperties;
	// Optional only because RBC types dayColumnWrapper as a prop-less ComponentType; DayColumn always passes it.
	date?: Date;
	resource?: string | null;
}

// RBC's own wrapper plus the column anchor: the practitioner in day view, the date in the week view, where `resource` is null.
const DayColumnBody = React.forwardRef<HTMLDivElement, DayColumnBodyProps>(({ children, className, style, date, resource }, ref) => (
	<div ref={ref} className={className} style={style} data-testid={resource === null || resource === undefined ? (date ? `appointments-week-column-${toDateKey(date)}` : undefined) : `appointments-column-${resource}`}>
		{children}
	</div>
));
DayColumnBody.displayName = "DayColumnBody";

interface PractitionerCalendarProps {
	events: Event[];
	teamMembers: TeamMember[];      // the practitioners that get a column in the day view
	allTeamMembers: TeamMember[];   // full roster, for resolving an event's practitioner
	date: Date;
	viewMode: CalendarViewMode;
	minHour: number;
	maxHour: number;
	slotMinutes: number;
	overrides?: Record<UserUuid, ScheduleOverride[]>;
	blocks?: Record<UserUuid, UnavailableBlock[]>;
	clinicClosures?: UnavailableBlock[];
	canManageSchedule?: boolean;
	currentUserId?: UserUuid | null;
	// The block whose write is in flight; a second drag on it would race the first into a wrong position.
	mutatingBlockKey?: string | null;
	// Same reason as mutatingBlockKey: a second drag would race the first into a wrong position.
	mutatingAppointmentId?: number | null;
	onEventClick: (event: Event) => void;
	onSlotClick: (date: Date, practitionerId?: UserUuid) => void;
	onSlotSelect?: (selection: SlotSelection) => void;
	onBlockEdit?: (ref: BlockRef) => void;
	onBlockMove?: (ref: BlockRef, move: EventMove) => void;
	// S12b's half of the same dispatch — passing it is what makes appointments draggable.
	onAppointmentMove?: (event: Event, move: EventMove) => void;
	onNavigate?: (date: Date) => void;
}

const NO_OVERRIDES: Record<UserUuid, ScheduleOverride[]> = {};
const NO_BLOCKS: Record<UserUuid, UnavailableBlock[]> = {};
const NO_CLOSURES: UnavailableBlock[] = [];

const DragAndDropCalendar = withDragAndDrop<CalendarEvent, ResourceColumn>(Calendar as React.ComponentType<CalendarProps<CalendarEvent, ResourceColumn>>);

const columnOf = (value?: string | number | null): UserUuid | null => {
	const id = value == null ? "" : String(value);
	return id !== "" && id !== UNASSIGNED_RESOURCE_ID ? id : null;
};

const PractitionerCalendar: React.FC<PractitionerCalendarProps> = ({ events, teamMembers, allTeamMembers, date, viewMode, minHour, maxHour, slotMinutes, overrides = NO_OVERRIDES, blocks = NO_BLOCKS, clinicClosures = NO_CLOSURES, canManageSchedule = false, currentUserId = null, mutatingBlockKey = null, mutatingAppointmentId = null, onEventClick, onSlotClick, onSlotSelect, onBlockEdit, onBlockMove, onAppointmentMove, onNavigate }) => {
	// The page re-renders on every search keystroke, so a bare console.warn in a render path spams.
	const warnedUnresolvedRef = useRef<Set<string>>(new Set());
	const warnUnresolvedOnce = (key: string, event: string, context: Record<string, unknown>) => {
		if (warnedUnresolvedRef.current.has(key)) return;
		warnedUnresolvedRef.current.add(key);
		console.warn(event, context);
	};

	const practitionerFor = useCallback(
		(event: Event): TeamMember | null => {
			const appt = event.resource.appointment;
			const member = allTeamMembers.find((tm) => tm.id === appt.practitioner_id) ?? null;
			if (!member) {
				warnUnresolvedOnce(`primary:${appt.id}:${appt.practitioner_id}`, "appointments.calendar.practitioner_unresolved", { appointmentId: appt.id, practitionerId: appt.practitioner_id });
			}
			return member;
		},
		[allTeamMembers]
	);

	const columnIds = useMemo(() => new Set<string>(teamMembers.map((member) => member.id)), [teamMembers]);

	// RBC accepts an array of ids, so a tandem appointment appears in every assigned column.
	// Background events run through this same accessor (TimeGrid groups them with resources.groupEvents), which is how a block lands in its practitioner's column.
	const resourceIdsFor = useCallback(
		(event: CalendarEvent): string[] => {
			if (isUnavailable(event)) return event.resource.columnIds;

			const appt = event.resource.appointment;
			const assigned: UserUuid[] = [appt.practitioner_id, ...((appt.practitioners ?? []) as any[]).map((p) => p.practitioner_id)];
			const matched = Array.from(new Set(assigned.filter((id) => columnIds.has(id))));
			return matched.length > 0 ? matched : [UNASSIGNED_RESOURCE_ID];
		},
		[columnIds]
	);

	const resources = useMemo((): ResourceColumn[] | undefined => {
		if (viewMode !== "day") return undefined;
		const columns: ResourceColumn[] = teamMembers.map((member) => ({ id: member.id, member, title: `${member.first_name} ${member.last_name}`.trim() }));
		const hasUnassigned = events.some((event) => resourceIdsFor(event)[0] === UNASSIGNED_RESOURCE_ID);
		return hasUnassigned ? [...columns, { id: UNASSIGNED_RESOURCE_ID, member: null, title: "Unassigned" }] : columns;
	}, [viewMode, teamMembers, events, resourceIdsFor]);

	const days = useMemo(() => {
		if (viewMode === "day") return [new Date(date.getFullYear(), date.getMonth(), date.getDate())];
		const monday = startOfCalendarWeek(date, "week");
		return Array.from({ length: 7 }, (_, index) => new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + index));
	}, [viewMode, date]);

	// Prebuilt once per (column, day) so slotPropGetter — which RBC calls per slot per column — never scans an override or block array.
	const availability = useMemo(() => {
		const index = new Map<string, DayAvailability>();

		teamMembers.forEach((member) => {
			const id = member.id;
			days.forEach((day) => index.set(indexKey(id, day), resolveDayAvailability(member, day, overrides[id], blocks[id], clinicClosures)));
		});

		// The all-staff week mutes only where nobody is rostered and carries closures alone — a personal block is a foreground card (D3), never shading, even with one practitioner ticked.
		const aggregateFor = (day: Date, entries: DayAvailability[]): DayAvailability => {
			const scheduled = mergeIntervals(entries.flatMap((entry) => entry.scheduled));
			const unavailable = resolveDayAvailability(null, day, undefined, undefined, clinicClosures).unavailable;

			return {
				dayOff: entries.length > 0 && entries.every((entry) => entry.dayOff),
				dayOffReason: entries.length === 1 ? entries[0].dayOffReason : null,
				scheduled,
				working: subtractIntervals(scheduled, unavailable),
				offDuty: subtractIntervals([{ start: 0, end: MINUTES_PER_DAY }], scheduled),
				unavailable,
			};
		};

		if (viewMode !== "day") {
			days.forEach((day) => {
				const entries = teamMembers.map((member) => index.get(indexKey(member.id, day))).filter((entry): entry is DayAvailability => Boolean(entry));
				index.set(indexKey(AGGREGATE_COLUMN, day), aggregateFor(day, entries));
			});
		}

		return index;
	}, [teamMembers, days, viewMode, overrides, blocks, clinicClosures]);

	// The stored rows behind the rendered regions — a drag needs the block's real instants, not the day-clipped ones.
	const blockRefs = useMemo(() => {
		const index = new Map<string, BlockRef>();

		const add = (record: UnavailableBlock, source: UnavailableSource, userId: UserUuid | null) =>
			index.set(blockRefKey({ id: record.id, source }), {
				id: record.id,
				userId,
				source,
				blockType: normaliseBlockType(record.block_type),
				reason: record.reason ?? null,
				start: dayjs(record.starts_at).toDate(),
				end: dayjs(record.ends_at).toDate(),
			});

		Object.entries(blocks).forEach(([userId, list]) => (list ?? []).forEach((record) => add(record, "block", userId)));
		clinicClosures.forEach((record) => add(record, "closure", null));

		return index;
	}, [blocks, clinicClosures]);

	const backgroundEvents = useMemo((): UnavailableEvent[] => {
		const allColumnIds = teamMembers.map((member) => member.id);
		const out: UnavailableEvent[] = [];

		const push = (day: Date, ids: UserUuid[], visual: BlockVisual, start: number, end: number, key: string, ref: BlockRef | null) => out.push(unavailableEvent(day, ids, visual, start, end, key, ref, null));

		const emit = (day: Date, ids: UserUuid[], entry: DayAvailability, scope: string) => {
			if (entry.dayOff) push(day, ids, { kind: "day-off", blockType: "other", reason: entry.dayOffReason }, 0, MINUTES_PER_DAY, `day-off-${scope}-${toDateKey(day)}`, null);
			entry.unavailable.forEach((interval) =>
				push(
					day,
					ids,
					{ kind: interval.source, blockType: interval.blockType, reason: interval.reason },
					interval.start,
					interval.end,
					`${interval.source}-${interval.id}-${scope}-${toDateKey(day)}`,
					blockRefs.get(blockRefKey(interval)) ?? null
				)
			);
		};

		days.forEach((day) => {
			if (viewMode === "day") {
				teamMembers.forEach((member) => {
					const entry = availability.get(indexKey(member.id, day));
					if (entry) emit(day, [member.id], entry, member.id);
				});
				return;
			}

			const entry = availability.get(indexKey(AGGREGATE_COLUMN, day));
			if (entry) emit(day, allColumnIds, entry, AGGREGATE_COLUMN);
		});

		return out;
	}, [availability, blockRefs, teamMembers, days, viewMode]);

	// D3: the all-staff week has no column to shade, so a ticked practitioner's blocks are foreground cards that carry their colour and name instead.
	const weekBlockEvents = useMemo((): UnavailableEvent[] => {
		if (viewMode === "day") return [];

		return teamMembers.flatMap((member) => {
			const id = member.id;
			return days.flatMap((day) =>
				(availability.get(indexKey(id, day))?.unavailable ?? [])
					.filter((interval) => interval.source === "block")
					.map((interval) =>
						// A card ending at 24:00 is next-day 00:00 to RBC, which files it as all-day into the hidden allday row; 23:59 keeps it in the column (and, as a clipped slice, unmovable).
						unavailableEvent(day, [], { kind: "block", blockType: interval.blockType, reason: interval.reason }, interval.start, Math.min(interval.end, MINUTES_PER_DAY - 1), `block-${interval.id}-${id}-${toDateKey(day)}`, blockRefs.get(blockRefKey(interval)) ?? null, id)
					)
			);
		});
	}, [availability, blockRefs, teamMembers, days, viewMode]);

	const calendarEvents = useMemo((): CalendarEvent[] => (viewMode === "week-all" ? [...events, ...weekBlockEvents] : events), [events, weekBlockEvents, viewMode]);

	// The card's practitioner is the ticked member it was built from, so a miss here needs no warning.
	const blockPractitionerFor = useCallback(
		(event: UnavailableEvent): TeamMember | null => (event.resource.practitionerId === null ? null : teamMembers.find((member) => member.id === event.resource.practitionerId) ?? null),
		[teamMembers]
	);

	// A Map get plus a test against a merged interval list that is at most a handful of entries long.
	const slotPropGetter = useCallback(
		(slotDate: Date, resourceId?: number | string | null) => {
			const entry = availability.get(indexKey(resourceId == null ? AGGREGATE_COLUMN : String(resourceId), slotDate));
			if (!entry) return {};

			return intervalsContain(entry.offDuty, slotDate.getHours() * 60 + slotDate.getMinutes()) ? { className: "appointments-slot-off" } : {};
		},
		[availability]
	);

	const { min, max } = useMemo(() => {
		const clamp = (hour: number) => Math.max(0, Math.min(hour, 23));
		return {
			min: new Date(date.getFullYear(), date.getMonth(), date.getDate(), clamp(minHour), 0, 0),
			max: new Date(date.getFullYear(), date.getMonth(), date.getDate(), clamp(maxHour), 59, 59),
		};
	}, [date, minHour, maxHour]);

	// Mirrors the server gate: a closure is a scheduling manager's, a personal block is theirs or a manager's.
	const canEditBlock = useCallback(
		(ref: BlockRef): boolean => (ref.userId === null ? canManageSchedule : canManageSchedule || ref.userId === currentUserId),
		[canManageSchedule, currentUserId]
	);

	// A region clipped to the rendered day is only a slice; dragging it would PUT the slice over the whole stored range.
	const isWholeBlock = (event: UnavailableEvent, ref: BlockRef): boolean => ref.start.getTime() === event.start.getTime() && ref.end.getTime() === event.end.getTime();

	const isMovable = useCallback(
		(event: CalendarEvent): boolean => {
			// The drawer's Save gate, so what can be dragged and what can be rescheduled stay one answer.
			if (!isUnavailable(event)) return Boolean(onAppointmentMove) && canRescheduleAppointment(event.resource.appointment) && mutatingAppointmentId !== event.id;

			const ref = event.resource.block;
			// A clinic closure is drawn in every column at once, so "which column did you drag it from" has no answer — it is edited, never dragged.
			if (!ref || ref.source === "closure" || !onBlockMove) return false;

			return canEditBlock(ref) && mutatingBlockKey !== blockRefKey(ref) && isWholeBlock(event, ref);
		},
		[onAppointmentMove, onBlockMove, canEditBlock, mutatingBlockKey, mutatingAppointmentId]
	);

	const eventPropGetter = useCallback(
		(event: CalendarEvent) => {
			if (isUnavailable(event)) {
				const ref = event.resource.block;
				const editable = Boolean(ref && onBlockEdit && canEditBlock(ref));
				const owner = blockPractitionerFor(event);
				const className = [blockClassName(event.resource.unavailable), owner ? "appointments-block--personal" : "", editable ? "appointments-block--editable" : "", isMovable(event) ? "appointments-block--movable" : ""].filter(Boolean).join(" ");
				return owner ? { className, style: { "--appt-block-prac": practitionerColour(owner) } as React.CSSProperties } : { className };
			}

			const member = practitionerFor(event);
			const colour = member ? practitionerColour(member) : NEUTRAL_BACKGROUND;
			return { style: { backgroundColor: colour, borderColor: colour, color: getReadableTextColor(colour) } };
		},
		[practitionerFor, blockPractitionerFor, onBlockEdit, canEditBlock, isMovable]
	);

	const dayOffBadge = useCallback((entry?: DayAvailability, testId?: string) => {
		if (!entry?.dayOff) return null;
		return (
			<span className="appointments-day-off-badge" data-testid={testId}>
				<StopOutlined />
				{entry.dayOffReason || "Unavailable"}
			</span>
		);
	}, []);

	const components = useMemo(
		() => ({
			event: ({ event }: { event: CalendarEvent }) => {
				if (isUnavailable(event)) return <UnavailableBlockCard visual={event.resource.unavailable} member={blockPractitionerFor(event)} />;

				const member = practitionerFor(event);
				return (
					<div className="appointments-calendar-event" data-testid={`appointments-event-${event.id}`}>
						<span className="appointments-calendar-event-time">{formatLocalTime(event.start)}</span>
						<span className="appointments-calendar-event-title">{event.title}</span>
						<span className="appointments-calendar-event-prac">{member ? `${member.first_name} ${member.last_name}` : "Unknown practitioner"}</span>
					</div>
				);
			},
			resourceHeader: ({ resource }: { resource: ResourceColumn }) => {
				const colour = resource.member ? practitionerColour(resource.member) : NEUTRAL_BACKGROUND;
				return (
					<div className="appointments-calendar-resource" data-testid={`appointments-column-header-${resource.id}`} style={{ borderBottomColor: colour }}>
						<PractitionerAvatar member={resource.member} size={20} />
						<span className="appointments-calendar-resource-name" style={{ color: colour }}>
							{resource.title}
						</span>
						{dayOffBadge(availability.get(indexKey(resource.id, date)), `appointments-day-off-${resource.id}`)}
					</div>
				);
			},
			// RBC hides this row in day view (.rbc-time-header-cell-single-day), so it is the week view's day-off badge.
			header: ({ date: headerDate, label }: { date: Date; label: string }) => (
				<>
					<span>{label}</span>
					{dayOffBadge(availability.get(indexKey(AGGREGATE_COLUMN, headerDate)), `appointments-day-off-${toDateKey(headerDate)}`)}
				</>
			),
			dayColumnWrapper: DayColumnBody,
		}),
		[practitionerFor, blockPractitionerFor, availability, date, dayOffBadge]
	);

	const handleSelectEvent = useCallback(
		(event: CalendarEvent) => {
			if (!isUnavailable(event)) {
				onEventClick(event);
				return;
			}

			const ref = event.resource.block;
			if (ref && canEditBlock(ref)) onBlockEdit?.(ref);
		},
		[onEventClick, onBlockEdit, canEditBlock]
	);

	// ⚠️ Dispatch on event KIND before anything reaches an API — a block id on the appointment endpoint (or the reverse) is what this exists to stop.
	const dispatchEventChange = useCallback(
		({ event, start, end, resourceId }: EventInteractionArgs<CalendarEvent>, action: "move" | "resize") => {
			const move: EventMove = { start: dayjs(start).toDate(), end: dayjs(end).toDate(), toColumnId: columnOf(resourceId), action };

			if (isUnavailable(event)) {
				const ref = event.resource.block;
				if (ref) onBlockMove?.(ref, move);
				return;
			}

			onAppointmentMove?.(event, move);
		},
		[onBlockMove, onAppointmentMove]
	);

	const handleEventDrop = useCallback((args: EventInteractionArgs<CalendarEvent>) => dispatchEventChange(args, "move"), [dispatchEventChange]);
	const handleEventResize = useCallback((args: EventInteractionArgs<CalendarEvent>) => dispatchEventChange(args, "resize"), [dispatchEventChange]);

	const handleSelectSlot = useCallback(
		(slot: SlotInfo) => {
			const column = columnOf(slot.resourceId as string | number | null | undefined) ?? undefined;

			// A zero-length "select" is a tap RBC mis-reports, not a zero-minute block.
			if (!onSlotSelect || slot.action !== "select" || slot.end.getTime() <= slot.start.getTime()) {
				onSlotClick(slot.start, column);
				return;
			}

			const practitionerId = viewMode === "day" ? column ?? null : null;

			// slot.slots are the covered slot STARTS, so a drag ending at midnight still reads as one day.
			const dayKeys = new Set((slot.slots?.length ? slot.slots : [slot.start, slot.end]).map(toDateKey));

			onSlotSelect({
				start: slot.start,
				end: slot.end,
				practitionerId,
				anchor: { left: slot.bounds?.right ?? 0, top: slot.bounds?.top ?? 0 },
				spansDays: dayKeys.size > 1,
			});
		},
		[onSlotClick, onSlotSelect, viewMode]
	);

	return (
		<div style={{ height: "83vh" }} className={`appointments-calendar appointments-calendar--${viewMode}`} data-testid="appointments-calendar">
			<DragAndDropCalendar
				localizer={calendarLocalizer}
				events={calendarEvents}
				backgroundEvents={backgroundEvents}
				resources={resources}
				resourceIdAccessor={(resource) => resource.id}
				resourceTitleAccessor={(resource) => resource.title}
				resourceAccessor={resourceIdsFor}
				date={date}
				onNavigate={(next) => onNavigate?.(next)}
				view={viewMode === "day" ? "day" : "week"}
				onView={() => undefined}
				views={["day", "week"]}
				toolbar={false}
				selectable
				step={slotMinutes}
				timeslots={60 / slotMinutes}
				min={min}
				max={max}
				scrollToTime={min}
				// RBC's default overlaps a week column's concurrent events into unreadable slivers.
				dayLayoutAlgorithm={viewMode === "day" ? "overlap" : "no-overlap"}
				formats={{ dayFormat: "ddd D", timeGutterFormat: "h:mm A" }}
				components={components}
				eventPropGetter={eventPropGetter}
				slotPropGetter={slotPropGetter}
				onSelectEvent={handleSelectEvent}
				onSelectSlot={handleSelectSlot}
				draggableAccessor={isMovable}
				resizableAccessor={isMovable}
				onEventDrop={handleEventDrop}
				onEventResize={handleEventResize}
			/>
		</div>
	);
};

export default PractitionerCalendar;
